import { Loading } from 'notiflix'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { useMemo, useState } from 'react'
import { FaArrowLeft, FaDownload, FaSyncAlt } from 'react-icons/fa'
import api from '../../../helpers/apiRequester'
import { useBatchDetail } from './hooks/useBatchPolling'
import { csvEscape, downloadText } from './shared'
import styles from './styles.module.scss'
import {
    BATCH_SOURCE_LABELS,
    BatchItemRow,
    BatchSource,
    CLAIM_RULE_LABELS,
    ClaimRule,
    batchStatusTone,
    formatDateTime,
    itemStatusLabel,
    itemStatusTone,
} from './types'

type ItemFilter = 'all' | 'unsigned' | 'signed' | 'viewed' | 'failed' | 'queued' | 'skipped'

const FILTER_OPTIONS: { value: ItemFilter; label: string }[] = [
    { value: 'all', label: 'Everyone' },
    { value: 'unsigned', label: 'Not yet signed' },
    { value: 'viewed', label: 'Opened, not signed' },
    { value: 'signed', label: 'Signed / delivered' },
    { value: 'failed', label: 'Failed' },
    { value: 'queued', label: 'Still queued' },
    { value: 'skipped', label: 'Skipped rows' },
]

const isUnsigned = (item: BatchItemRow) =>
    item.status === 'sent' &&
    item.delivery_type !== 'attachment' &&
    (item.contract_status === 'Sent' || item.contract_status === 'Viewed')

const isSigned = (item: BatchItemRow) =>
    item.status === 'sent' && (item.contract_status === 'Signed' || item.contract_status === 'Delivered')

const matchesFilter = (item: BatchItemRow, filter: ItemFilter) => {
    switch (filter) {
        case 'all':
            return true
        case 'unsigned':
            return isUnsigned(item)
        case 'viewed':
            return item.status === 'sent' && item.contract_status === 'Viewed'
        case 'signed':
            return isSigned(item)
        default:
            return item.status === filter
    }
}

interface BatchDetailProps {
    batchId: number
    onBack: () => void
    onChanged: () => void
}

export const BatchDetail = ({ batchId, onBack, onChanged }: BatchDetailProps) => {
    const { batch, items, loaded, error, refresh } = useBatchDetail(batchId)
    const [filter, setFilter] = useState<ItemFilter>('all')
    const [search, setSearch] = useState('')
    const [busyId, setBusyId] = useState<number | null>(null)

    const counts = useMemo(() => {
        const sent = items.filter((i) => i.status === 'sent')
        return {
            total: items.length,
            queued: items.filter((i) => i.status === 'queued').length,
            skipped: items.filter((i) => i.status === 'skipped').length,
            failed: items.filter((i) => i.status === 'failed').length,
            sent: sent.length,
            viewed: sent.filter((i) => i.contract_status === 'Viewed').length,
            signed: items.filter(isSigned).length,
            unsigned: items.filter(isUnsigned).length,
        }
    }, [items])

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase()
        return items.filter((item) => {
            if (!matchesFilter(item, filter)) return false
            if (!q) return true
            return [item.recipient_name, item.recipient_email, item.case_ref, item.client_origin, item.error]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(q))
        })
    }, [items, filter, search])

    const isEsign = batch?.delivery_type !== 'attachment'
    const progress = batch && batch.total > 0 ? Math.min(100, Math.round((batch.processed / batch.total) * 100)) : 0

    const startBatch = () => {
        if (!batch) return
        Confirm.show(
            'Start bulk send',
            `Send ${counts.queued} ${isEsign ? 'signing links' : 'documents'} now?`,
            'Start',
            'Cancel',
            async () => {
                Loading.circle('Starting...')
                try {
                    const response = await api({ url: 'contract-sign/bulk/start', data: { batch_id: batch.id } })
                    Loading.remove()
                    if (!response.data?.success) {
                        return Report.failure('Error', response.data?.message || 'Could not start the send.', 'Okay')
                    }
                    await refresh()
                    onChanged()
                } catch (e: any) {
                    Loading.remove()
                    Report.failure('Error', e?.response?.data?.message || 'Could not start the send.', 'Okay')
                }
            },
        )
    }

    const resendUnsigned = () => {
        if (!batch) return
        Confirm.show(
            'Resend to unsigned',
            `Resend the signing link to all ${counts.unsigned} recipients who have not signed yet?`,
            'Resend',
            'Cancel',
            async () => {
                Loading.circle('Resending...')
                try {
                    const response = await api({
                        url: 'contract-sign/bulk/resend-unsigned',
                        data: { batch_id: batch.id },
                    })
                    Loading.remove()
                    if (!response.data?.success) {
                        return Report.failure('Error', response.data?.message || 'Could not resend.', 'Okay')
                    }
                    Report.success(
                        'Resent',
                        `${response.data.sent} of ${response.data.total} signing links were re-sent.${
                            response.data.failed ? ` ${response.data.failed} could not be sent.` : ''
                        }`,
                        'Okay',
                    )
                } catch (e: any) {
                    Loading.remove()
                    Report.failure('Error', e?.response?.data?.message || 'Could not resend.', 'Okay')
                }
            },
        )
    }

    const resendOne = (item: BatchItemRow) => {
        if (!item.contract_id) return
        Confirm.show('Resend email', `Resend to ${item.recipient_email}?`, 'Resend', 'Cancel', async () => {
            setBusyId(item.id)
            try {
                const response = await api({ url: 'contract-sign/resend', data: { contract_id: item.contract_id } })
                if (!response.data?.success) {
                    return Report.failure('Error', response.data?.message || 'Could not resend the email.', 'Okay')
                }
                Report.success('Sent', `The email has been resent to ${item.recipient_email}.`, 'Okay')
            } catch (e: any) {
                Report.failure('Error', e?.response?.data?.message || 'Could not resend the email.', 'Okay')
            } finally {
                setBusyId(null)
            }
        })
    }

    const openDoc = async (item: BatchItemRow, which: 'original' | 'signed') => {
        if (!item.contract_id) return
        setBusyId(item.id)
        try {
            const response = await api({ url: 'contract-sign/get-doc', data: { contract_id: item.contract_id, which } })
            if (!response.data?.success || !response.data.url) {
                return Report.failure('Error', response.data?.message || 'Could not open the document.', 'Okay')
            }
            window.open(response.data.url, '_blank', 'noopener,noreferrer')
        } catch (e: any) {
            Report.failure('Error', e?.response?.data?.message || 'Could not open the document.', 'Okay')
        } finally {
            setBusyId(null)
        }
    }

    const exportCsv = () => {
        if (!batch) return
        const header = [
            'Row',
            'Name',
            'Email',
            'Source',
            'Client ID',
            'Case ref',
            'Status',
            'Viewed',
            'Signed',
            'Error',
            'Signing link',
        ]
        const lines = filtered.map((item) =>
            [
                item.row_index,
                item.recipient_name,
                item.recipient_email,
                item.client_origin || (item.merge_values ? 'Spreadsheet' : ''),
                item.client_id,
                item.case_ref,
                itemStatusLabel(item),
                item.viewed_at ? formatDateTime(item.viewed_at) : '',
                item.signed_at ? formatDateTime(item.signed_at) : '',
                item.error,
                item.signing_link,
            ]
                .map(csvEscape)
                .join(','),
        )
        const name =
            (batch.name || `bulk-send-${batch.id}`).replace(/[^a-z0-9-_ ]/gi, '').trim() || `bulk-send-${batch.id}`
        downloadText(`${name}${filter === 'all' ? '' : `-${filter}`}.csv`, [header.join(','), ...lines].join('\n'))
    }

    if (!loaded) {
        return (
            <section className={styles.panel}>
                <div className={styles.emptyState}>Loading bulk send...</div>
            </section>
        )
    }

    if (error || !batch) {
        return (
            <section className={styles.panel}>
                <div className={styles.errorBanner}>{error || 'Bulk send not found.'}</div>
                <button type="button" className={styles.secondaryBtn} onClick={onBack}>
                    <FaArrowLeft /> Back to bulk sends
                </button>
            </section>
        )
    }

    const summary: { label: string; value: string | number; tone?: string }[] = [
        { label: 'Recipients', value: batch.total },
        { label: 'Sent', value: counts.sent },
        ...(isEsign
            ? [
                  { label: 'Signed', value: counts.signed, tone: counts.signed > 0 ? 'toneGood' : undefined },
                  {
                      label: 'Not yet signed',
                      value: counts.unsigned,
                      tone: counts.unsigned > 0 ? 'toneAccent' : undefined,
                  },
                  { label: 'Opened, not signed', value: counts.viewed },
              ]
            : []),
        { label: 'Failed', value: counts.failed, tone: counts.failed > 0 ? 'toneBad' : undefined },
        ...(counts.skipped ? [{ label: 'Skipped rows', value: counts.skipped }] : []),
        ...(counts.queued ? [{ label: 'Queued', value: counts.queued }] : []),
    ]

    return (
        <>
            <section className={styles.panel}>
                <div className={styles.panelHeader}>
                    <div className={styles.panelHeaderText}>
                        <h4 className={styles.panelTitle}>
                            {batch.name || `Bulk send #${batch.id}`}
                            <span className={`${styles.statusPill} ${styles[batchStatusTone(batch.status)]}`}>
                                {batch.status === 'running' ? `Running · ${progress}%` : batch.status}
                            </span>
                        </h4>
                        <p className={styles.panelBody}>
                            {batch.template_name || 'Template'} ·{' '}
                            {BATCH_SOURCE_LABELS[batch.source as BatchSource] || batch.source} ·{' '}
                            {batch.granularity === 'claim' ? 'one document per claim' : 'one document per client'}
                            {batch.granularity === 'client' && batch.claim_rule
                                ? ` (${CLAIM_RULE_LABELS[batch.claim_rule as ClaimRule] || batch.claim_rule})`
                                : ''}
                            {' · '}
                            {isEsign ? 'signing links' : 'PDF attachments'} · created {formatDateTime(batch.created_at)}
                            {batch.created_by ? ` by ${batch.created_by}` : ''}
                            {batch.finished_at ? ` · finished ${formatDateTime(batch.finished_at)}` : ''}
                        </p>
                        {batch.last_error && <div className={styles.errorBanner}>{batch.last_error}</div>}
                    </div>
                    <div className={styles.rowActions}>
                        <button type="button" className={styles.secondaryBtn} onClick={onBack}>
                            <FaArrowLeft /> All bulk sends
                        </button>
                        <button type="button" className={styles.secondaryBtn} onClick={() => refresh()}>
                            <FaSyncAlt /> Refresh
                        </button>
                        {batch.status !== 'running' && counts.queued > 0 && (
                            <button type="button" className={styles.primaryBtn} onClick={startBatch}>
                                {batch.status === 'draft'
                                    ? `Start send (${counts.queued})`
                                    : `Send remaining (${counts.queued})`}
                            </button>
                        )}
                        {isEsign && counts.unsigned > 0 && batch.status !== 'running' && (
                            <button type="button" className={styles.secondaryBtn} onClick={resendUnsigned}>
                                Resend to unsigned ({counts.unsigned})
                            </button>
                        )}
                    </div>
                </div>

                {batch.status === 'running' && (
                    <div
                        className={styles.progressBar}
                        role="progressbar"
                        aria-valuenow={progress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                    >
                        <span style={{ width: `${progress}%` }} />
                    </div>
                )}

                <div className={styles.miniStats} style={{ marginTop: 16 }}>
                    {summary.map((stat) => (
                        <div key={stat.label} className={styles.miniStat}>
                            <span className={`${styles.miniStatValue} ${stat.tone ? styles[stat.tone] : ''}`}>
                                {stat.value}
                            </span>
                            <span className={styles.miniStatLabel}>{stat.label}</span>
                        </div>
                    ))}
                </div>
            </section>

            <div className={styles.toolbar}>
                <input
                    className={styles.searchInput}
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Filter by name, email, case ref or error…"
                />
                <select
                    className={styles.toolbarSelect}
                    value={filter}
                    onChange={(e) => setFilter(e.target.value as ItemFilter)}
                    aria-label="Filter recipients"
                >
                    {FILTER_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
                <button
                    type="button"
                    className={styles.secondaryBtn}
                    onClick={exportCsv}
                    disabled={filtered.length === 0}
                >
                    <FaDownload /> Export {filter === 'all' ? 'all' : 'filtered'} ({filtered.length})
                </button>
            </div>

            <p className={styles.resultLine}>
                Showing {filtered.length} of {items.length} recipients
            </p>

            <div className={styles.tableWrap}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Recipient</th>
                            <th>Source</th>
                            <th>Claim</th>
                            <th>Status</th>
                            {isEsign && <th>Viewed</th>}
                            {isEsign && <th>Signed</th>}
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr>
                                <td colSpan={isEsign ? 8 : 6}>
                                    <div className={styles.emptyState}>No recipients match this filter.</div>
                                </td>
                            </tr>
                        ) : (
                            filtered.map((item) => {
                                const busy = busyId === item.id
                                const signed = isSigned(item)
                                return (
                                    <tr key={item.id} className={styles.staticRow}>
                                        <td className={styles.muted}>{item.row_index}</td>
                                        <td>
                                            <div className={styles.stackCell}>
                                                <span className={styles.stackPrimary}>
                                                    {item.recipient_name || item.recipient_email || '—'}
                                                </span>
                                                {item.recipient_name && (
                                                    <span className={styles.stackSecondary}>
                                                        {item.recipient_email}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`${styles.pill} ${styles.muted}`}>
                                                {item.client_origin
                                                    ? `${item.client_origin} · #${item.client_id}`
                                                    : 'Spreadsheet'}
                                            </span>
                                        </td>
                                        <td className={styles.nowrap}>{item.case_ref || '—'}</td>
                                        <td>
                                            <div className={styles.stackCell}>
                                                <span
                                                    className={`${styles.statusPill} ${styles[itemStatusTone(item)]}`}
                                                >
                                                    {itemStatusLabel(item)}
                                                </span>
                                                {item.error && (
                                                    <span className={styles.stackSecondary}>{item.error}</span>
                                                )}
                                            </div>
                                        </td>
                                        {isEsign && <td className={styles.nowrap}>{formatDateTime(item.viewed_at)}</td>}
                                        {isEsign && <td className={styles.nowrap}>{formatDateTime(item.signed_at)}</td>}
                                        <td>
                                            {item.contract_id ? (
                                                <div className={styles.rowActions}>
                                                    <button
                                                        type="button"
                                                        className={styles.compactBtn}
                                                        disabled={busy}
                                                        onClick={() => openDoc(item, signed ? 'signed' : 'original')}
                                                    >
                                                        {signed ? 'Signed copy' : 'Open PDF'}
                                                    </button>
                                                    {!signed && (
                                                        <button
                                                            type="button"
                                                            className={styles.compactBtn}
                                                            disabled={busy}
                                                            onClick={() => resendOne(item)}
                                                        >
                                                            Resend
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className={styles.muted}>—</span>
                                            )}
                                        </td>
                                    </tr>
                                )
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </>
    )
}
